import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Footer from './Footer';

const Privacy = () => {
  useEffect(() => {
    document.title = "Privacy | Panda AI";
  }, []);

  return (
    <div className="about-page policy-page">
      <div className="about-inner">

        <motion.section
          className="about-intro-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="about-main-title">PRIVACY POLICY</h1>
          <p className="about-main-subtitle">
            Effective Date: October 2, 2026
          </p>
        </motion.section>

        <hr className="section-divider" />

        <motion.section
          className="policy-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="policy-article">
            <p>
              This Privacy Policy covers both Panda apps from Steepwind Labs: <strong>Panda</strong> on the Google Play Store (package <code>com.blurr.voice</code>) and <strong>Panda Automate</strong> (package <code>com.steepwind.panda.shortcut</code>). They share one login and one account. Some features exist only in Automate. Where a section applies to one app, we say so.
            </p>

            <h2>1. Your Account</h2>
            <p>
              You sign in with Google, an email one-time code, or Microsoft. We store your Firebase user ID, email, display name, plan, and usage credits on our servers, tied to that account. The same account works in both apps. Signing in on Automate does not copy the session from Panda — you sign in once in each app, then you see the same account.
            </p>
            <p>
              We also keep a random device ID on the phone for local session bookkeeping. That ID is not a substitute for your signed-in account.
            </p>

            <h2>2. Information We Collect</h2>
            <p>
              We only collect what is needed to run the features you use. Play Store Panda does not request SMS, call log, contacts write, background location, or package-install permissions. Automate does, for the features below.
            </p>

            <h3>a) Screen Content (via Accessibility Service)</h3>
            <p>
              Both apps can use the Android Accessibility Service so Panda can understand what is on screen and act on it. That may include:
            </p>
            <ul>
              <li><strong>UI element information:</strong> text, content descriptions, and resource IDs.</li>
              <li><strong>Screen structure:</strong> the layout of the screen.</li>
              <li><strong>Screenshots:</strong> when visual context is needed for an AI request you started.</li>
            </ul>

            <h3>b) Audio (Microphone)</h3>
            <p>The <code>RECORD_AUDIO</code> permission is used to:</p>
            <ul>
              <li><strong>Voice commands:</strong> spoken audio is sent to our speech-to-text providers (Sarvam, and Google where used) so we can turn it into text.</li>
              <li><strong>Wake word (Play Store Panda):</strong> listening for “Panda” can run on the device with OpenWakeWord or Picovoice Porcupine. Ambient audio for wake-word detection is not sent to our servers.</li>
            </ul>
            <p>
              Panda Automate is built around extensions and automations. The in-app voice agent and wake word are not the main path on that build.
            </p>
            <p>
              Spoken replies may use Google Cloud text-to-speech (and optionally Sarvam) through our servers.
            </p>

            <h3>c) Conversations</h3>
            <p>
              Commands you type or speak, and the back-and-forth in a session, are sent to our AI providers so we can carry out the request. Recent conversation text and session stats may be stored under your account (not as anonymous logs).
            </p>

            <h3>d) Memories</h3>
            <p>
              Facts you add, or that we extract from a conversation to personalize later replies, are stored on our servers under your account so they survive reinstalls and show up in both apps. You can view and delete them in the app.
            </p>

            <h3>e) Installed Apps</h3>
            <p>
              With <code>QUERY_ALL_PACKAGES</code>, Panda can match an app name to open it. We do not sell that list.
            </p>

            <h3>f) Location</h3>
            <p>
              Precise and approximate location can be used when you ask something that needs it (for example weather nearby) or when you create a location automation. Background location is used only in Automate, and only to watch geofences you set (for example “when I get home”). Location used for a request may be included in the AI prompt. Geofence coordinates you set are stored on the device and also backed up with your automation rules (see section 3).
            </p>

            <h3>g) Contacts, SMS, Calls, Calendar, Media, Notifications, Clipboard</h3>
            <p>
              These are read or written only to do something you asked — look someone up, send a text, create an event, list recent photos, read a notification, copy text — except as noted for Automate SMS in section 3. Relevant bits may be included in the AI prompt for that request. We do not keep a standing copy of your inbox, call log, or photo library on our servers.
            </p>
            <p>
              Play Store Panda does not ship the SMS, call-log, or contacts-write permissions.
            </p>

            <h3>h) Device Status</h3>
            <p>
              Battery, Wi‑Fi name and signal, Bluetooth pairings, volume, and alarms can be read locally when you ask, or used by automations you created in Automate.
            </p>

            <h3>i) On-Device Files</h3>
            <p>
              Notes, task scratch files, extension drafts, and similar files stay in the app’s private storage. Uninstalling the app deletes them.
            </p>

            <h3>j) Account Telemetry</h3>
            <ul>
              <li><strong>Crash reports:</strong> Firebase Crashlytics may include your user ID and email so we can fix a crash.</li>
              <li><strong>Analytics:</strong> sign-in, briefing, and purchase events via Firebase Analytics.</li>
              <li><strong>Performance:</strong> Firebase Performance traces for slow screens and network calls.</li>
              <li><strong>Remote Config:</strong> feature flags from Firebase, not personal content.</li>
              <li><strong>Push token:</strong> an FCM token so we can send notifications you opted into.</li>
              <li><strong>Accessibility signals:</strong> whether TalkBack or similar services are on, so we can improve the product for people who use them.</li>
            </ul>

            <h3>k) Purchases and Credits</h3>
            <p>
              If you buy Pro or use credits, plan and remaining credits are stored on our servers under your account. Play billing is verified against the store listing you bought from.
            </p>

            <h2>3. Panda Automate Only</h2>

            <h3>a) Extensions</h3>
            <p>
              Automate can run small programs (bundled with the app or ones you install or write in Ext Studio). They run on your phone. Depending on what you allow, an extension can read the accessibility tree of the current screen, show an overlay, store small key-value data, and — if its manifest asks for it — call websites or connected services (for example Gmail via Composio). Ext Studio sends your building instructions to our AI so it can write the extension. Extension files live on the device; we do not remotely push new extension code onto your phone without you installing it.
            </p>

            <h3>b) Automations and Triggers</h3>
            <p>
              Rules you create (Wi‑Fi name, location, charging, schedule, SMS keyword, and similar) are saved on the phone and also backed up to your account so we can restore them. If you publish a rule to the community, the rule definition is shared with other users. After reboot, Automate reschedules alarms and geofences you already set.
            </p>

            <h3>c) UPI Spending Tracker</h3>
            <p>
              When this feature is on, incoming bank or UPI debit SMS messages are read automatically (not only when you ask). The message body may be sent to our AI — which may use web search — to categorize the payment. Transactions, including the SMS text, are stored in a local database on your phone. Daily summary text may be generated with the same AI path. We do not upload that local spending database to Firestore.
            </p>

            <h3>d) On-Device AI Model</h3>
            <p>
              You can optionally download a Gemma language model (from Hugging Face, or via Google’s on-device AI stack where available). The file stays on the phone. Inference runs on the device. That download can be a few gigabytes and is meant for Wi‑Fi.
            </p>

            <h3>e) Built-In Browser and Video Helpers</h3>
            <p>
              Automate can open pages inside the app (GeckoView), including a YouTube overlay with a local ad blocker (uBlock Origin). We do not log your browsing history to our servers. Optional helpers send a YouTube video ID to SponsorBlock (<code>sponsor.ajay.app</code>) and Return YouTube Dislike so skip segments and dislike counts can show.
            </p>

            <h2>4. How We Use Your Information</h2>
            <ul>
              <li>To run the features you asked for — voice, screen actions, automations, extensions, spending tracker.</li>
              <li>To keep your account, memories, credits, and (on Automate) automation backups in sync across devices and reinstalls.</li>
              <li>To debug crashes and improve reliability, including anonymized or account-linked logs of AI calls.</li>
              <li>To process purchases and enforce plan limits.</li>
            </ul>
            <p>
              On the free plan, your data is used to train our models. On Pro and Bring Your Own Key, your data is used only if you choose that.
            </p>
            <p>We do not sell your data.</p>

            <h2>5. Third-Party Services</h2>
            <p>Panda uses a small set of specialists. Your use of those features is also subject to their policies.</p>
            <ul>
              <li><strong>Google (Gemini and related AI):</strong> conversations, screen context, and other prompt material needed for a request you started.</li>
              <li><strong>Google Firebase:</strong> Auth, Firestore account data, Crashlytics, Analytics, Performance, Remote Config, Cloud Messaging.</li>
              <li><strong>Google Cloud Text-to-Speech:</strong> spoken replies.</li>
              <li><strong>Sarvam AI:</strong> speech-to-text (and optional speech synthesis) through our servers.</li>
              <li><strong>Tavily:</strong> web search when a task needs research.</li>
              <li><strong>Composio:</strong> optional connections (Gmail, Calendar, and similar). Credentials stay with Composio; we send only the instruction needed for your request.</li>
              <li><strong>Picovoice Porcupine:</strong> on-device wake word on Play Store Panda.</li>
              <li><strong>Hugging Face:</strong> optional Gemma model download on Automate.</li>
              <li><strong>Google ML Kit / AI Core:</strong> on-device Gemma where the phone supports it.</li>
              <li><strong>Microsoft:</strong> sign-in if you choose that option.</li>
              <li><strong>Mozilla GeckoView:</strong> in-app browser engine on Automate.</li>
              <li><strong>SponsorBlock and Return YouTube Dislike:</strong> video IDs only, on Automate video features.</li>
            </ul>

            <h2>6. Your Choices</h2>
            <ul>
              <li><strong>Memories:</strong> view, add, or delete them in the app.</li>
              <li><strong>Permissions:</strong> turn off Accessibility, Microphone, Location, SMS, and the rest in Android Settings. That turns off the matching feature.</li>
              <li><strong>Connected services:</strong> disconnect Composio connectors in the app.</li>
              <li><strong>Automate features:</strong> turn off UPI tracking, individual automations, or uninstall an extension.</li>
              <li><strong>Account:</strong> contact us if you want the cloud account data deleted.</li>
            </ul>

            <h2>7. Changes</h2>
            <p>
              We may update this policy. The new version will be posted on this page.
            </p>

            <h2>8. Contact</h2>
            <p>
              Questions:{' '}
              <a href="mailto:support@heypanda.org" className="policy-link">
                support@heypanda.org
              </a>
            </p>
          </div>
        </motion.section>
      </div>

      <Footer />

      <style jsx>{`
        .policy-page {
          background: transparent;
        }

        .policy-article {
          max-width: 800px;
          margin: 0 auto;
        }

        .policy-article h2 {
          font-size: 1.5rem;
          color: var(--primary-color);
          margin-top: 40px;
          margin-bottom: 20px;
          padding-bottom: 8px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .policy-article h2:first-child {
          margin-top: 0;
        }

        .policy-article h3 {
          font-size: 1.15rem;
          color: var(--text-primary);
          margin-top: 24px;
          margin-bottom: 12px;
        }

        .policy-article p {
          font-size: 1.05rem;
          line-height: 1.8;
          color: var(--text-secondary);
          margin-bottom: 16px;
        }

        .policy-article ul {
          margin-bottom: 24px;
          padding-left: 24px;
        }

        .policy-article li {
          font-size: 1.05rem;
          line-height: 1.8;
          color: var(--text-secondary);
          margin-bottom: 8px;
        }

        .policy-article li strong {
          color: var(--text-primary);
        }

        .policy-article code {
          background: rgba(255, 255, 255, 0.08);
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
          color: var(--primary-color);
        }

        .policy-link {
          color: var(--primary-color);
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .policy-link:hover {
          color: var(--text-primary);
          text-decoration: underline;
        }

        @media (max-width: 768px) {
          .policy-article h2 {
            font-size: 1.25rem;
          }
          .policy-article h3 {
            font-size: 1.05rem;
          }
          .policy-article p,
          .policy-article li {
            font-size: 0.95rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Privacy;
